import { makeStyles, tokens, Text, Title3, Badge } from '@fluentui/react-components';
import type {
  ExecutiveSummaryDoc,
  ExecSafetyVerdictNum,
  ExecFinding,
  ExecPersonaBreak,
} from '../api/simulation';
import { SAFETY_VERDICT_LABEL } from '../api/simulation';

// Presentational executive summary panel for Phase 7 M6.
// Mirrors ExecutiveRecommendation card chrome:
//   bordered card, colorNeutralBackground1, borderRadius 8px, padding 20px.

export interface ExecutiveSummaryPanelProps {
  summary: ExecutiveSummaryDoc;
  onDrillIn: (resultId: string) => void;
}

type BadgeColor = 'success' | 'warning' | 'danger';

function verdictBadgeColor(v: ExecSafetyVerdictNum): BadgeColor {
  if (v === 0) return 'success';
  if (v === 2) return 'danger';
  return 'warning';
}

const SEVERITY_COLOR: Record<ExecFinding['severity'], BadgeColor> = {
  high: 'danger',
  medium: 'warning',
  low: 'success',
};

const useStyles = makeStyles({
  card: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  headline: {
    marginBottom: '16px',
    paddingBottom: '14px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground1,
    fontSize: '15px',
    lineHeight: '1.5',
  },
  sectionLabel: {
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: tokens.colorNeutralForeground2,
    marginBottom: '8px',
    marginTop: '14px',
    display: 'block',
  },
  riskRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px 10px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    background: tokens.colorNeutralBackground2,
    marginBottom: '6px',
    cursor: 'default',
  },
  riskRowClickable: {
    cursor: 'pointer',
    border: `1px solid ${tokens.colorBrandStroke2}`,
  },
  riskMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
    minWidth: 0,
  },
  riskTitle: {
    fontWeight: 600,
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
  },
  riskDetail: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    marginTop: '2px',
  },
  personaRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '6px 10px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    background: tokens.colorNeutralBackground2,
    marginBottom: '6px',
    cursor: 'default',
  },
  personaRowClickable: {
    cursor: 'pointer',
    border: `1px solid ${tokens.colorBrandStroke2}`,
  },
  recList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    paddingLeft: '16px',
    margin: 0,
    listStyle: 'disc',
  },
  recItem: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.5',
  },
  footer: {
    marginTop: '14px',
    paddingTop: '12px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  drillLink: {
    marginLeft: 'auto',
    color: tokens.colorBrandForegroundLink,
    fontSize: '12px',
    flexShrink: 0,
  },
});

export function ExecutiveSummaryPanel({ summary, onDrillIn }: ExecutiveSummaryPanelProps) {
  const s = useStyles();
  const verdictLabel = SAFETY_VERDICT_LABEL[summary.safetyVerdict];
  const badgeColor = verdictBadgeColor(summary.safetyVerdict);

  return (
    <div className={s.card}>
      {/* Top row: title + verdict badge */}
      <div className={s.topRow}>
        <Title3 as="h2">Executive Summary</Title3>
        <Badge color={badgeColor} size="large" appearance="filled">
          {verdictLabel}
        </Badge>
      </div>

      {/* Headline */}
      <div className={s.headline}>{summary.headline}</div>

      {/* Top risks */}
      {summary.topRisks.length > 0 && (
        <>
          <span className={s.sectionLabel}>Top risks</span>
          {summary.topRisks.map((risk: ExecFinding, i: number) => {
            const clickable = risk.evidenceResultId !== null;
            return (
              <div
                key={i}
                className={`${s.riskRow} ${clickable ? s.riskRowClickable : ''}`}
                onClick={clickable ? () => onDrillIn(risk.evidenceResultId as string) : undefined}
                {...(clickable ? { role: 'button' as const, tabIndex: 0 } : {})}
                onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onDrillIn(risk.evidenceResultId as string); } : undefined}
              >
                <Badge color={SEVERITY_COLOR[risk.severity]} appearance="tint" size="small">
                  {risk.severity}
                </Badge>
                <div className={s.riskMeta}>
                  <span className={s.riskTitle}>{risk.title}</span>
                  <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                    {risk.persona}
                  </Text>
                  <span className={s.riskDetail}>{risk.detail}</span>
                </div>
                {clickable && (
                  <span className={s.drillLink}>View trace →</span>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* Personas that broke it */}
      {summary.personasThatBrokeIt.length > 0 && (
        <>
          <span className={s.sectionLabel}>Personas that broke it</span>
          {summary.personasThatBrokeIt.map((pb: ExecPersonaBreak, i: number) => {
            const clickable = pb.evidenceResultId !== null;
            return (
              <div
                key={i}
                className={`${s.personaRow} ${clickable ? s.personaRowClickable : ''}`}
                onClick={clickable ? () => onDrillIn(pb.evidenceResultId as string) : undefined}
                {...(clickable ? { role: 'button' as const, tabIndex: 0 } : {})}
                onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onDrillIn(pb.evidenceResultId as string); } : undefined}
              >
                <div className={s.riskMeta}>
                  <span className={s.riskTitle}>{pb.persona}</span>
                  <span className={s.riskDetail}>{pb.whatBroke}</span>
                </div>
                {clickable && (
                  <span className={s.drillLink}>View trace →</span>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* Recommendations */}
      {summary.recommendations.length > 0 && (
        <>
          <span className={s.sectionLabel}>Recommendations</span>
          <ul className={s.recList}>
            {summary.recommendations.map((rec: string, i: number) => (
              <li key={i} className={s.recItem}>{rec}</li>
            ))}
          </ul>
        </>
      )}

      {/* Footer: model + tokens + cost */}
      <div className={s.footer}>
        <span>{summary.modelUsed}</span>
        <span>·</span>
        <span>{summary.inputTokens.toLocaleString()} in / {summary.outputTokens.toLocaleString()} out tok</span>
        <span>·</span>
        <span>${summary.costUsd.toFixed(6)}</span>
        <span>·</span>
        <span>{new Date(summary.generatedAt).toLocaleString()}</span>
      </div>
    </div>
  );
}
